import React, { useState, useContext, useEffect } from 'react';
import Hotspot from '@/v2/components/atoms/Hotspot';
import HotspotBig from '@/v2/components/atoms/Icon/HotspotBig';
import Button from '@/v2/components/atoms/Button';
import Fade from '@/v2/components/atoms/Fade';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import Header from '@/v2/components/application/Header';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { DesignContext } from '@/v2/state/providers';
import { StateContext } from '@/v2/state/store';
import { actionStrings, MomentStateContext } from '@/v2/state/momentState';
import { ClearMemory, IsIE11Win7 } from '@/v2/components/utils/DeviceUtils/DeviceUtils';
import { timeout, clearTimeouts } from '@/v2/components/utils/Timeout';

import './Hotspots.scss';

const HOTSPOT_DELAY = 1400;
const VISITED_STORAGE_KEY = 'visitedHotspots';
const MAX_VISITED_BEFORE_RELOAD = 3;

const hotspotSubjectText = (id, data) => {
    const hotspotArray = (data.hotspots && data.hotspots.filter((item) => item.id === id)) || [];

    return hotspotArray[0] && hotspotArray[0].text.subject.toLowerCase();
};

const handleVisited = (index, visited, setVisited, momentDispatch, totalHotSpotCount) => {
    const visitedArray = [...new Set([...visited, index])];

    momentDispatch({
        type: actionStrings.activeMomentProgress,
        payload: visitedArray.length * (100 / totalHotSpotCount),
    });
    setVisited(visitedArray);
};

const handleClicked = (index, clicked, setClicked) => {
    const clickedArray = [...clicked];

    clickedArray.push(index);
    setClicked(clickedArray);
};

const removeAnimationDone = (setIconAnimationDone, setHoverTransition) => {
    setIconAnimationDone(false);
    setHoverTransition(false);
};

export default ({ isActive, data, onFinished, onReady }) => {
    const [activeId, setActiveId] = useState(null);
    const [visited, setVisited] = useState([]);
    const [finished, setFinished] = useState(false);
    const [fadeToBlack, setFadeToBlack] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [showHotspot, setShowHotspot] = useState(false);
    const [hotspotSeen, setHotspotSeen] = useState(false);
    const [visitedCount, setVisitedCount] = useState(0);
    const [stopHotspotAnimate, setStopHotspotAnimate] = useState(false);
    const [headerShow, setHeaderShow] = useState(true);
    const [clicked, setClicked] = useState([]);
    const [animateOut, setAnimateOut] = useState('');
    const [iconAnimationDone, setIconAnimationDone] = useState(false);
    const [hoverTransition, setHoverTransition] = useState(false);
    const { trackEvent } = useContext(AnalyticsContext);
    const [{ disclaimer }] = useContext(DesignContext);
    const store = useContext(StateContext);
    const { dispatch, state } = store;
    const { dispatch: momentDispatch } = useContext(MomentStateContext);

    let fadeIn = '';

    if (isActive) {
        fadeIn = 'Hotspots__fadeIn';
        timeout(() => {
            setShowHotspot(true);
        }, HOTSPOT_DELAY);
    }

    const ref = React.useRef();

    const hotspotTrackEvent = (id) => {
        const hotspotName = hotspotSubjectText(id, data);

        trackEvent('vcClick', {
            vcStep: 'design',
            vcClick: `hotspot:${hotspotName}`,
        });
    };

    const openHotspot = (id, analyticsObject) => {
        if (id) {
            hotspotTrackEvent(id, analyticsObject);

            timeout(() => {
                setShowNext(false);
            }, 700);

            timeout(() => {
                setHeaderShow(false);
            }, 1300);

            timeout(() => {
                setFadeToBlack(true);
            }, 2000);

            timeout(() => {
                setStopHotspotAnimate(true);
                setActiveId(id);
                setHotspotSeen(true);
            }, 2800);
        } else {
            setFadeToBlack(false);
            setActiveId(null);
        }
    };

    const onHotspotFinished = () => {
        setHeaderShow(true);

        timeout(() => {
            setStopHotspotAnimate(false);
            setAnimateOut('');
            setClicked([]);
        }, 700);

        timeout(() => {
            setShowNext(true);
        }, 2600);

        openHotspot(null);

        // Store visited hotspots before
        // reloading the page in IE11Win7
        if (visitedCount === MAX_VISITED_BEFORE_RELOAD && IsIE11Win7()) {
            if (window.localStorage) {
                window.localStorage.setItem(VISITED_STORAGE_KEY, visited.toString());
            }

            ClearMemory();
        }
    };

    const proceed = () => {
        setAnimateOut('animate-out');
        setShowNext(false);
        setFinished(true);

        timeout(() => {
            setHeaderShow(false);
        }, 1000);

        timeout(() => {
            setFadeToBlack(true);
        }, 1800);

        timeout(() => {
            onFinished();
        }, 2800);

        if (!state.lightBgEnabled) {
            dispatch({ type: 'SET_BACKGROUND_LIGHT' });
        }
    };

    const animationDoneCallback = () => {
        setIconAnimationDone(true);

        timeout(() => {
            setHoverTransition(true);
        }, 100);
    };

    useEffect(() => {
        onReady();

        const { visitedHotspots } = window.localStorage;

        // Check localStorage for visited hotspots
        // and perform any required updates
        if (visitedHotspots) {
            const visitedHotspotsArr = visitedHotspots.split(',').map((indexStr) => parseInt(indexStr, 10));

            setShowNext(true);
            setHotspotSeen(true);
            setVisited(visitedHotspotsArr);

            window.localStorage.removeItem(VISITED_STORAGE_KEY);
        }

        return () => clearTimeouts();
    }, []);

    const headerContent = <Header show={headerShow && isActive} animate hideLogo />;
    const disclaimerContent = isActive && disclaimer ? <Disclaimer data={disclaimer} /> : '';

    const icons = !isActive ? (
        ''
    ) : (
        <div className="Hotspots__container">
            {data.hotspots &&
                data.hotspots.map((item, index) => (
                    <button
                        ref={ref}
                        style={{ top: `${item.position.top}%`, left: `${item.position.left}%` }}
                        className={[
                            'Hotspots__icons',
                            visited.indexOf(index) > -1 ? 'visited' : '',
                            animateOut,
                            iconAnimationDone ? 'animation-done' : '',
                            hoverTransition ? 'add-transition' : '',
                            clicked.indexOf(index) > -1 ? 'clicked' : '',
                        ].join(' ')}
                        onClick={() => {
                            setVisitedCount(visitedCount + 1);
                            handleVisited(index, visited, setVisited, momentDispatch, data?.hotspots?.length);
                            setAnimateOut('animate-out');
                            handleClicked(index, clicked, setClicked);
                            openHotspot(item.id);
                        }}
                        key={item.id}
                        type="button"
                        tabIndex="0"
                    >
                        {showHotspot ? (
                            <HotspotBig
                                loop={false}
                                isstopped={stopHotspotAnimate}
                                ispaused={stopHotspotAnimate}
                                eventListeners={[
                                    {
                                        eventName: 'complete',
                                        callback: () => animationDoneCallback(setIconAnimationDone, setHoverTransition),
                                    },
                                ]}
                            />
                        ) : (
                            ''
                        )}
                    </button>
                ))}
        </div>
    );

    const ctas =
        !isActive || !hotspotSeen ? (
            ''
        ) : (
            <div className="Hotspots__cta">
                <Button
                    variant="primary-light"
                    animate={showNext ? 'in' : 'out'}
                    onClick={() => {
                        proceed(onFinished, setFinished, dispatch, state, setAnimateOut, setShowNext, setHeaderShow, setFadeToBlack);
                    }}
                >
                    {data.text.cta.nextMoment}
                </Button>
            </div>
        );

    return (
        <div className={['Hotspots', fadeIn].join(' ')}>
            <div className="Hotspots__main">
                <div className="Hotspots__inner">
                    <img className="Hotspots__bg" src={data.image} alt="Hotspots" />
                    {icons}
                </div>
                {headerContent}
                {ctas}
                <Hotspot
                    data={data}
                    activeId={activeId}
                    onFinished={() => {
                        onHotspotFinished(
                            setActiveId,
                            setFadeToBlack,
                            setShowNext,
                            setClicked,
                            setStopHotspotAnimate,
                            setHeaderShow,
                            setAnimateOut,
                            visitedCount,
                            visited
                        );
                        removeAnimationDone(setIconAnimationDone, setHoverTransition);
                    }}
                />
                <Fade show={fadeToBlack} light={finished} cover />
                {disclaimerContent}
            </div>
        </div>
    );
};
